import HBBoardNav from "@/components/HBBoardNav";
import HBCategoryNav from "@/components/HBCategoryNav";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { useInspirationBoards } from "@/hooks/useInspirationBoards";
import { shouldGoToFollowPage } from "@/utils/getHomeUrl";
import useUrlState from "@/utils/useUrlState";
import { omit } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

const FILTER_CATEGORIES = ["funny", "beauty"]; // 分类过滤掉美女和搞笑

export default function BoardNav() {
  const router = useRouter();
  const { user, categories } = useAppStore((s) => ({
    user: s.user,
    categories: s.categories,
  }));

  // 使用自定义 Hook 获取画板数据（带缓存和去重）
  const { boards: inspirationBoards, loading: boardsLoading } =
    useInspirationBoards(user);

  // 登录后如果有画板推荐，且当前在分类页（有 categoryId），则跳转到发现页
  // 避免出现 URL 是分类页但显示的是画板导航的情况
  useEffect(() => {
    if (
      user &&
      inspirationBoards &&
      inspirationBoards.length > 0 &&
      !boardsLoading
    ) {
      const currentCategoryId = router.query.categoryId;
      const currentPathname = router.pathname;
      // 只在发现页的分类路由下进行跳转，不影响其他页面
      if (
        currentCategoryId &&
        currentPathname === "/discovery/[categoryId]" &&
        currentCategoryId !== "all"
      ) {
        // 使用 setTimeout 将路由跳转放到下一个事件循环
        // 避免阻塞当前渲染
        setTimeout(() => {
          router.replace("/discovery");
        }, 0);
      }
    }
  }, [user, inspirationBoards, boardsLoading, router]);

  // 判断是否还在加载中
  const isLoading = user && boardsLoading;

  // 判断是否显示画板导航
  // 1. 加载中且已登录 → 显示画板导航（空列表，只有"发现"）
  // 2. 加载完成且有画板数据 → 显示画板导航（完整列表）
  const showBoardNav =
    (isLoading && user) || (user && inspirationBoards.length > 0);

  const [queryParam] = useUrlState();
  let { categoryId = null, tabId = "pins", board = null } = router.query;
  const { pathname } = router;

  // 根据路由确定当前选中的值
  const currentValue = useMemo(() => {
    if (pathname === "/discovery/boards/[board]" && board) {
      return board; // 画板详情页，返回画板 ID
    }
    return categoryId; // 其他页面返回分类 ID
  }, [pathname, board, categoryId]);

  const routeMap = {
    follow: "/follow",
    discovery: "/discovery",
    guide: "/guide",
  };

  const initialValue = useMemo(() => {
    const currentPath = router.pathname;
    if (
      currentPath.includes(routeMap.follow) ||
      currentPath.includes(routeMap.guide)
    ) {
      return "follow";
    }
    return null; // 默认值
  }, [router.pathname, routeMap.follow, routeMap.guide]);

  const extraOption = useMemo(() => {
    return user ? { text: "关注", value: "follow" } : undefined;
  }, [user]);

  // 分类导航的回调函数
  const onChangeCategoryNav = useCallback(
    (category) => {
      // 如果点击了同一个tab，则不进行路由跳转，只触发数据刷新事件
      const newCategoryId = category?.value || null;
      if (newCategoryId === "follow" && pathname === "/follow") {
        // 点击关注tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.FOLLOW_PIN_LIST_RESET));
        return;
      } else if (newCategoryId === null && pathname === "/discovery") {
        // 点击发现tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      } else if (
        newCategoryId === categoryId &&
        pathname === "/discovery/[categoryId]"
      ) {
        // 点击同一个分类，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      }

      if (category && category.value === "follow") {
        // 检查用户是否应该跳转到关注页
        if (user && shouldGoToFollowPage(user)) {
          router.push("/follow");
        } else {
          router.push("/guide");
        }
        return;
      }
      if (tabId === "pins") {
        const param = omit(queryParam, ["categoryId"]);
        router.push({
          pathname: category ? `/discovery/${category.value}` : "/discovery",
          query: {
            ...param,
          },
        });
      } else {
        router.push({
          pathname: category
            ? `/discovery/${category.value}/${tabId}`
            : `/discovery/all/${tabId}`,
        });
      }
    },
    [queryParam, router, tabId, user, categoryId, pathname]
  );

  // 画板导航的回调函数
  const onChangeBoardNav = useCallback(
    (board) => {
      // 如果点击了同一个tab，则不进行路由跳转，只触发数据刷新事件
      const newBoardId = board?.value || null;
      if (newBoardId === "follow" && pathname === "/follow") {
        // 点击关注tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.FOLLOW_PIN_LIST_RESET));
        return;
      } else if (newBoardId === null && pathname === "/discovery") {
        // 已经在发现页，点击发现tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      } else if (
        newBoardId === router.query.board &&
        pathname === "/discovery/boards/[board]"
      ) {
        // 点击同一个画板，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      }

      if (board && board.value === "follow") {
        // 检查用户是否应该跳转到关注页
        if (user && shouldGoToFollowPage(user)) {
          router.push("/follow");
        } else {
          router.push("/guide");
        }
        return;
      }

      // 画板导航默认都是针对 pins，不需要考虑 tabId
      if (board?.value) {
        router.push(`/discovery/boards/${board.value}`);
      } else {
        router.push("/discovery");
      }
    },
    [router, user, pathname]
  );

  // 根据条件渲染不同的导航组件
  if (showBoardNav) {
    // 画板导航 - 已登录 && (加载中 || 有画板推荐列表)
    return (
      <HBBoardNav
        allText={"发现"}
        extraOption={extraOption}
        baseHref="/discovery"
        onChange={onChangeBoardNav}
        value={currentValue || initialValue}
        boards={isLoading ? [] : inspirationBoards} // 加载中传空数组，只显示"发现"
      />
    );
  } else {
    // 分类导航 - 未登录 || 无画板推荐列表
    return (
      <HBCategoryNav
        allText={"发现"}
        extraOption={extraOption}
        options={isLoading ? [] : categories} // 加载中传空数组，只显示"发现"
        filterValues={FILTER_CATEGORIES}
        baseHref="/discovery"
        onChange={onChangeCategoryNav}
        value={categoryId || initialValue}
      />
    );
  }
}
